// src/pages/goal-setting/dashboard/GoalCard.tsx
import { Card, CardContent, Box } from "@mui/material";
import { useMemo, useState } from "react";
import {
  GoalDashboardBaseline,
  GoalDashboardBaseYear,
  GoalItem,
} from "../../../../models/GoalSettingDashboard";
import { useTranslation } from "react-i18next";
import { common, reduce } from "../../../../i18n";
import CustomGoalSettingChart from "./CustomGoalSettingChart";
import { GridColDef, GridColumnGroupingModel } from "@mui/x-data-grid";
import NumberFormatterUtils from "../../../../utils/numberFormatterUtils";
import { renderColoredNumberCell } from "../../../../utils/renderCellUtils";

interface Props {
  goalData: GoalItem;
}

export const GoalSettingCard = ({ goalData }: Props) => {
  const { t } = useTranslation([common, reduce], { nsMode: "fallback" });
  const [showNonPlanned, setShowNonPlanned] = useState<boolean>(true);

  const {
    columns,
    columnGroupingModel,
    rows,
  }: {
    columns: GridColDef[];
    columnGroupingModel: GridColumnGroupingModel;
    rows: (GoalDashboardBaseline | GoalDashboardBaseYear)[];
  } = useMemo(() => {
    if (goalData.methodology === "baseline") {
      // ---------------------- BASELINE ----------------------

      const plans = goalData.plans as GoalDashboardBaseline[];
      const cols: GridColDef[] = [
        {
          field: "year",
          headerName: t("AD"),
          headerAlign: "center",
          align: "center",
          sortable: false,
          filterable: false,
          width: 80,
          renderCell: (params) => {
            const { value } = params;
            const isBaseYear = value === goalData.baseYear; // ✅ ตรวจปีฐาน

            return (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: isBaseYear ? "#DEDEDE" : "transparent", // 🩶 พื้นหลังสีเทา
                }}
              >
                {value}
              </Box>
            );
          },
        },
        {
          field: "percentAnnualGrowthRate",
          headerName: `% ${t("Annual-Growth-Rate")}`,
          headerAlign: "center",
          align: "center",
          sortable: false,
          filterable: false,
          flex: 1,
          minWidth: 160,
          valueFormatter: (value) =>
            value === null ? "" : NumberFormatterUtils.numberFormat(value),
        },
        {
          field: "bauEmission",
          renderHeader: () => (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              sx={{ lineHeight: 1.2 }}
            >
              <span>{t("Bau-Emission")}</span>
              <span>
                (<span dangerouslySetInnerHTML={{ __html: "tCO₂e" }} />)
              </span>
            </Box>
          ),
          headerAlign: "center",
          align: "right",
          sortable: false,
          filterable: false,
          flex: 1.5,
          minWidth: 160,
          valueFormatter: (value) =>
            value === null ? "" : NumberFormatterUtils.numberFormat(value),
        },
        {
          field: "percentReduction",
          headerName: `% ${t("Reduction")}`,
          headerAlign: "center",
          align: "center",
          sortable: false,
          filterable: false,
          flex: 1,
          minWidth: 120,
          valueFormatter: (value) =>
            value === null
              ? ""
              : `${NumberFormatterUtils.numberFormat(value)}%`,
        },
        {
          field: "targetReduction",
          renderHeader: () => (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              sx={{
                lineHeight: 1.2,
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              <span>{t("Target-Reduction")}</span>
              <span>
                (
                <span
                  dangerouslySetInnerHTML={{ __html: "tCO<sub>2</sub>e" }}
                />
                )
              </span>
            </Box>
          ),
          headerAlign: "center",
          align: "right",
          sortable: false,
          filterable: false,
          flex: 1.5,
          minWidth: 180,
          valueFormatter: (value) =>
            value === null ? "" : NumberFormatterUtils.numberFormat(value),
        },
        {
          field: "targetEmission",
          renderHeader: () => (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              sx={{
                lineHeight: 1.2,
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              <span>{t("Target-Emission")}</span>
              <span>
                (
                <span
                  dangerouslySetInnerHTML={{ __html: "tCO<sub>2</sub>e" }}
                />
                )
              </span>
            </Box>
          ),
          headerAlign: "center",
          align: "right",
          sortable: false,
          filterable: false,
          flex: 1.5,
          minWidth: 180,
          valueFormatter: (value) =>
            value === null ? "" : NumberFormatterUtils.numberFormat(value),
        },
        {
          field: "scope1",
          headerName: t("Scope-1"),
          headerAlign: "center",
          align: "right",
          sortable: false,
          filterable: false,
          flex: 1,
          minWidth: 120,
          valueFormatter: (value) =>
            value === null ? "" : NumberFormatterUtils.numberFormat(value),
        },
        {
          field: "scope2",
          headerName: t("Scope-2"),
          headerAlign: "center",
          align: "right",
          sortable: false,
          filterable: false,
          flex: 1,
          minWidth: 120,
          valueFormatter: (value) =>
            value === null ? "" : NumberFormatterUtils.numberFormat(value),
        },
        {
          field: "scope3",
          headerName: t("Scope-3"),
          headerAlign: "center",
          align: "right",
          sortable: false,
          filterable: false,
          flex: 1,
          minWidth: 120,
          valueFormatter: (value) =>
            value === null ? "" : NumberFormatterUtils.numberFormat(value),
        },
        {
          field: "total",
          headerName: t("Total"),
          headerAlign: "center",
          align: "right",
          sortable: false,
          filterable: false,
          flex: 1,
          minWidth: 120,
          valueFormatter: (value) =>
            value === null ? "" : NumberFormatterUtils.numberFormat(value),
        },
        {
          field: "baseYearReduction",
          headerName: "",
          flex: 1,
          minWidth: 120,
          align: "right",
          sortable: false,
          filterable: false,
          valueFormatter: (value) =>
            value === null ? "" : NumberFormatterUtils.numberFormat(value),
        },
        {
          field: "baseYearReductionPercent",
          headerName: "",
          flex: 1,
          minWidth: 120,
          align: "right",
          sortable: false,
          filterable: false,
          renderCell: ({ value }) =>
            renderColoredNumberCell(value, {
              showPlus: true,
              showPercent: true,
              reversePositiveToNegative: true,
            }),
        },
        {
          field: "targetVariance",
          renderHeader: () => (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              sx={{ lineHeight: 1.2 }}
            >
              <span>{t("Target-Variance")}</span>
              <span>
                (
                <span
                  dangerouslySetInnerHTML={{ __html: "tCO<sub>2</sub>e" }}
                />
                )
              </span>
            </Box>
          ),
          headerAlign: "center",
          align: "right",
          sortable: false,
          filterable: false,
          flex: 1.5,
          minWidth: 180,
          renderCell: ({ value }) =>
            renderColoredNumberCell(value, { showPlus: true }),
        },
        {
          field: "reductionVariance",
          headerName: "",
          flex: 1,
          minWidth: 100,
          align: "right",
          sortable: false,
          filterable: false,
          valueFormatter: (value) =>
            value === null ? "" : NumberFormatterUtils.numberFormat(value),
        },
        {
          field: "reductionVariancePercent",
          headerName: "",
          flex: 1,
          minWidth: 100,
          align: "right",
          sortable: false,
          filterable: false,
          renderCell: ({ value }) =>
            renderColoredNumberCell(value, {
              showPlus: true,
              showPercent: true,
              reversePositiveToNegative: true,
            }),
        },
      ];

      const group: GridColumnGroupingModel = [
        {
          groupId: "ActualEmissions",
          headerName: `${t("Actual-Emissions")}`,
          headerAlign: "center",
          children: [
            { field: "scope1" },
            { field: "scope2" },
            { field: "scope3" },
            { field: "total" },
          ],
        },
        {
          groupId: "BaseYearReduction",
          headerName: `${t("Base-Year-Reduction")} (tCO₂e)`,
          headerAlign: "center",
          children: [
            { field: "baseYearReduction" },
            { field: "baseYearReductionPercent" },
          ],
        },
        {
          groupId: "ReductionVariance",
          headerName: `${t("Reduction-Variance")} (tCO₂e)`,
          headerAlign: "center",
          children: [
            { field: "reductionVariance" },
            { field: "reductionVariancePercent" },
          ],
        },
      ];
      // ✅ ใช้ข้อมูลจริงจาก goalData.plans
      const rows =
        plans?.map((p, index) => ({
          id: index + 1,
          year: p.year,
          percentAnnualGrowthRate: p.percentAnnualGrowthRate,
          bauEmission: p.bauEmission,
          percentReduction: p.percentReduction,
          targetReduction: p.targetReduction,
          targetEmission: p.targetEmission,
          scope1: p.scope1,
          scope2: p.scope2,
          scope3: p.scope3,
          total: p.total,
          baseYearReduction: p.baseYearReduction,
          baseYearReductionPercent: p.baseYearReductionPercent,
          targetVariance: p.targetVariance,
          reductionVariance: p.reductionVariance,
          reductionVariancePercent: p.reductionVariancePercent,
        })) ?? [];

      return { columns: cols, columnGroupingModel: group, rows };
    } else {
      // ---------------------- BASEYEAR ----------------------
      const plans = goalData.plans as GoalDashboardBaseYear[];
      const cols: GridColDef[] = [
        {
          field: "year",
          headerName: t("AD"),
          headerAlign: "center",
          align: "center",
          sortable: false,
          filterable: false,
          width: 80,
          renderCell: (params) => {
            const { value } = params;
            const isBaseYear = value === goalData.baseYear; // ✅ ตรวจปีฐาน

            return (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: isBaseYear ? "#DEDEDE" : "transparent", // 🩶 พื้นหลังสีเทา
                }}
              >
                {value}
              </Box>
            );
          },
        },
        {
          field: "baseYearEmission",
          headerAlign: "center",
          align: "right",
          sortable: false,
          filterable: false,
          flex: 1.5,
          minWidth: 180,
          renderHeader: () => (
            <span>
              {t("Base-Year-Emission")} (
              <span dangerouslySetInnerHTML={{ __html: "tCO<sub>2</sub>e" }} />)
            </span>
          ),
          valueFormatter: (value) =>
            value === null ? "" : NumberFormatterUtils.numberFormat(value),
        },
        {
          field: "percentReduction",
          headerName: `% ${t("Reduction")}`,
          headerAlign: "center",
          align: "center",
          sortable: false,
          filterable: false,
          flex: 1,
          minWidth: 120,
          valueFormatter: (value) =>
            value === null
              ? ""
              : `${NumberFormatterUtils.numberFormat(value)}%`,
        },
        {
          field: "targetEmission",
          headerAlign: "center",
          align: "right",
          sortable: false,
          filterable: false,
          renderHeader: () => (
            <span>
              {t("Emission-Reduction")} (
              <span dangerouslySetInnerHTML={{ __html: "tCO<sub>2</sub>e" }} />)
            </span>
          ),
          flex: 1.5,
          minWidth: 180,
          valueFormatter: (value) =>
            value === null ? "" : NumberFormatterUtils.numberFormat(value),
        },
        {
          field: "targetReduction",
          headerAlign: "center",
          align: "right",
          sortable: false,
          filterable: false,
          renderHeader: () => (
            <span>
              {t("Target-Reduction")} (
              <span dangerouslySetInnerHTML={{ __html: "tCO<sub>2</sub>e" }} />)
            </span>
          ),
          flex: 1.5,
          minWidth: 180,
          valueFormatter: (value) =>
            value === null ? "" : NumberFormatterUtils.numberFormat(value),
        },
        {
          field: "scope1",
          headerName: t("Scope-1"),
          headerAlign: "center",
          align: "right",
          sortable: false,
          filterable: false,
          flex: 1,
          minWidth: 120,
          valueFormatter: (value) =>
            value === null ? "" : NumberFormatterUtils.numberFormat(value),
        },
        {
          field: "scope2",
          headerName: t("Scope-2"),
          headerAlign: "center",
          align: "right",
          sortable: false,
          filterable: false,
          flex: 1,
          minWidth: 120,
          valueFormatter: (value) =>
            value === null ? "" : NumberFormatterUtils.numberFormat(value),
        },
        {
          field: "scope3",
          headerName: t("Scope-3"),
          headerAlign: "center",
          align: "right",
          sortable: false,
          filterable: false,
          flex: 1,
          minWidth: 120,
          valueFormatter: (value) =>
            value === null ? "" : NumberFormatterUtils.numberFormat(value),
        },
        {
          field: "total",
          headerName: t("Total"),
          headerAlign: "center",
          align: "right",
          sortable: false,
          filterable: false,
          flex: 1,
          minWidth: 120,
          valueFormatter: (value) =>
            value === null ? "" : NumberFormatterUtils.numberFormat(value),
        },
        {
          field: "targetVariance",
          renderHeader: () => (
            <span>
              {t("Target-Variance")} (
              <span dangerouslySetInnerHTML={{ __html: "tCO<sub>2</sub>e" }} />)
            </span>
          ),
          headerAlign: "center",
          align: "right",
          sortable: false,
          filterable: false,
          flex: 1.5,
          minWidth: 180,
          renderCell: ({ value }) =>
            renderColoredNumberCell(value, { showPlus: true }),
        },
        {
          field: "reductionVariance",
          headerName: "",
          headerAlign: "center",
          align: "right",
          sortable: false,
          filterable: false,
          flex: 1.5,
          minWidth: 180,
          valueFormatter: (value) =>
            value === null ? "" : NumberFormatterUtils.numberFormat(value),
        },
        {
          field: "reductionVariancePercent",
          headerName: "",
          headerAlign: "center",
          align: "right",
          sortable: false,
          filterable: false,
          flex: 1.5,
          minWidth: 180,
          renderCell: ({ value }) =>
            renderColoredNumberCell(value, {
              showPlus: true,
              showPercent: true,
              reversePositiveToNegative: true,
            }),
        },
      ];

      const group: GridColumnGroupingModel = [
        {
          groupId: "ActualEmissions",
          headerAlign: "center",
          headerName: `${t("Actual-Emissions")} (tCO₂e)`,
          children: [
            { field: "scope1" },
            { field: "scope2" },
            { field: "scope3" },
            { field: "total" },
          ],
        },
        {
          groupId: "ReductionVariance",
          headerAlign: "center",
          headerName: `${t("Reduction-Variance")} (tCO₂e)`,
          children: [
            { field: "reductionVariance" },
            { field: "reductionVariancePercent" },
          ],
        },
      ];

      const rows =
        plans?.map((p, index) => ({
          id: index + 1,
          year: p.year,
          baseYearEmission: p.baseYearEmission ?? null,
          percentReduction: p.percentReduction ?? null,
          targetEmission: p.targetEmission ?? null,
          targetReduction: p.targetReduction ?? null,
          scope1: p.scope1 ?? null,
          scope2: p.scope2 ?? null,
          scope3: p.scope3 ?? null,
          total: p.total ?? null,
          targetVariance: p.targetVariance ?? null,
          reductionVariance: p.reductionVariance ?? null,
          reductionVariancePercent: p.reductionVariancePercent ?? null,
        })) ?? [];

      return { columns: cols, columnGroupingModel: group, rows };
    }
  }, [goalData.methodology, t]);

  const filteredRows = (() => {
    if (!rows?.length) return [];

    const { baseYear, startYear, targetYear } = goalData;

    // ✅ toggle เปิด → แสดงทุกปี
    if (showNonPlanned) return rows;

    // ✅ toggle ปิด → แสดงเฉพาะ baseYear + ช่วง startYear–targetYear
    return rows.filter((row) => {
      const year = Number(row.year);
      if (isNaN(year)) return false;
      return year === baseYear || (year >= startYear && year <= targetYear);
    });
  })();

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "8px",
        padding: "24px",
        ".MuiCardContent-root": {
          padding: "0px !important",
        },
      }}
    >
      <CardContent>
        <CustomGoalSettingChart
          goal={goalData}
          showNonPlanned={showNonPlanned}
          setShowNonPlanned={setShowNonPlanned}
          rows={filteredRows}
          columns={columns}
          columnGroupingModel={columnGroupingModel}
        />
      </CardContent>
    </Card>
  );
};
